import { Flex, Heading, IconArchiveOutline, Paragraph, Spacer, Spinner } from '@zillow/constellation';
import { Accordion } from '@zillow/zrs-rental-applications';
import { applicationStatusTypes, uiApplicationStatusTypes } from '../../constants/statusConstants';
import { calculateApplications, categorizeApplications } from '../../utils/listingApplicationListUtil';
import useQueryVariables from '../../hooks/useQueryVariables';
import ArchivedApplication from './ArchivedApplication';
import { findMostRecentApplicant } from '../../pages/ApplicationsTabPage/ListingApplicationsTab/ListingApplications/ListingApplicationsApplicationSection/ListingApplicationsCardList/ListingApplicationCard/ListingApplicationCardUtil';
import { useSelector } from 'react-redux';
import { getNeedsToIdVerifySelector } from '../../selectors';
import { LISTING_APPLICATIONS_QUERY } from '../../constants/queries';
import { useZgGraphqlQuery } from '../../hooks/useGraphqlQuery';
import { logError } from '@zg-rentals/log-error';
const { HIDDEN } = uiApplicationStatusTypes;

// returns an array of applications IDs excluding "HIDDEN" applications
export function categorizedApplicationMapToArray(categorizedApplicationMap = {}) {
  const { [HIDDEN.id]: _, ...nonHiddenApplications } = categorizedApplicationMap;

  return Object.values(nonHiddenApplications).flatMap((arrayOfIds) => arrayOfIds);
}

export function getDateAssociatedWithStatusType({ statusType, applicants, withdrawDate, declinedDate }) {
  if (statusType === applicationStatusTypes.ACTIVE.id) {
    const mostRecentApplicant = findMostRecentApplicant(applicants);
    return mostRecentApplicant.submitDate;
  }
  if (statusType === applicationStatusTypes.WITHDRAWN.id) {
    return withdrawDate;
  }
  if (statusType === applicationStatusTypes.DECLINED.id) {
    return declinedDate;
  }
}

export default function ArchivedApplicationsList() {
  const { propertyId } = useQueryVariables();
  const needsToIdVerify = useSelector(getNeedsToIdVerifySelector());

  const {
    isLoading,
    refetch: fetchInactiveApplications,
    data,
  } = useZgGraphqlQuery(
    LISTING_APPLICATIONS_QUERY,
    { listingAlias: propertyId, isActive: false, needsToIdVerify },
    { enabled: false },
  );

  const applications = calculateApplications(data, { includeInvitationsToApply: false });
  const { applicationMap, categorizedApplicationMap } = categorizeApplications(applications);
  const applicationIds = categorizedApplicationMapToArray(categorizedApplicationMap);

  const sortedApplications = applicationIds
    .map((id) => applicationMap[id])
    .sort((a, b) => {
      let aDate = getDateAssociatedWithStatusType(a);
      let bDate = getDateAssociatedWithStatusType(b);

      return aDate < bDate ? 1 : -1;
    });

  const archivedApplicationsDataRetentionPolicy = (
    <Paragraph fontColor="textMedium" marginBottom="lg">
      Applications shown here were declined, withdrawn, or are over 90 days old. They’ll be deleted after 2 years — save
      copies for your records before then.
    </Paragraph>
  );

  let accordionContent;
  if (isLoading) {
    accordionContent = (
      <Spacer as={Flex} padding="xs" display="flex" justifyContent="center">
        <Spinner size="lg" />
      </Spacer>
    );
  } else if (!data || !sortedApplications?.length) {
    accordionContent = archivedApplicationsDataRetentionPolicy;
  } else {
    accordionContent = (
      <>
        {archivedApplicationsDataRetentionPolicy}
        <>
          {sortedApplications.map((a) => (
            <ArchivedApplication key={a.applicationId} application={a} propertyId={propertyId} />
          ))}
        </>
      </>
    );
  }

  const onAccordionChange = async () => {
    const response = await fetchInactiveApplications();
    if (response.error) {
      logError({ context: 'Failed to fetch archived applications', error: response.error });
    }
  };

  return (
    <Accordion id="archived-applications" onChange={onAccordionChange}>
      <Accordion.Item>
        <Accordion.Header id="archived-applications__header">
          <Flex display="flex" alignItems="center">
            <IconArchiveOutline size="sm" />
            <Spacer as={Heading} marginLeft="xs" level="4">
              Archived applications
            </Spacer>
          </Flex>
        </Accordion.Header>
        <Accordion.Panel>{accordionContent}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
